import { getAuth } from "firebase/auth"
import { createRouter, createWebHistory } from "vue-router"

import { useAuthStore } from "@/components/Auth/AuthStore"
import { i18n } from "@/i18n"
import { isUserInitialized, myUser, tenantCompanies, tenantMembershipLoadState } from "@/init/authState"
import reportError from "@/utils/reportError"

import authenticatedRoutes from "./authenticated"
import authenticationRoutes from "./authentication"
import { canNavigateToRoute } from "./routePermissions"
import { shouldRedirectToInviteAuthenticated } from "./tenantMembershipRedirect"

const routes = [
  {
    path: "/start",
    name: "Start",
    redirect: () => {
      const authStore = useAuthStore()
      return authStore.loggedIn ? { name: "Index" } : { name: "Auth" }
    }
  },
  ...authenticationRoutes,
  ...authenticatedRoutes
]
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(async (to, from, next) => {
  await isUserInitialized // Wait for user initialization to complete

  const isLoggedIn = !!getAuth().currentUser?.uid
  const isAuthRoute = to.matched.some((route) => route.name === "Auth")
  const allowsGuests = to.matched.some((route) => route.meta?.allowUnauthenticated)
  const isInvitePublic = to.matched.some((route) => route.name === "InvitePublic")
  const isInviteAuthenticated = to.matched.some((route) => route.name === "InviteAuthenticated")
  const isUserProfile = to.path === "/profile"

  // Ensure /invite sends users to the correct variant
  if (isLoggedIn && isInvitePublic) {
    return next({ name: "InviteAuthenticated", query: to.query, hash: to.hash })
  }
  if (!isLoggedIn && isInviteAuthenticated) {
    return next({ name: "InvitePublic", query: to.query, hash: to.hash })
  }

  if (allowsGuests) {
    return next()
  }

  if (isLoggedIn) {
    if (isAuthRoute) return next({ name: "Index" })
    if (!canNavigateToRoute(to, myUser)) return next({ name: "Index" })

    if (to.path.toUpperCase().includes("TMP")) {
      const user = myUser
      if (user.loadingData) {
        await user.loadingData
      }
      if (!user.isRestored) return next({ name: "Index" })
    }

    const companyUsers = Array.isArray(myUser?.companyUsers) ? myUser.companyUsers : []
    const hasCompanyUsers = companyUsers.some((companyUser) => !companyUser?.deletedAt)

    const currentTenants = Array.isArray(tenantCompanies?.value) ? tenantCompanies.value : []
    const hasTenantCompanies = currentTenants.some((tenantItem) => !tenantItem?.isDeleted)

    const hasTenantMembership = hasCompanyUsers || hasTenantCompanies || !!myUser?.companyId

    if (
      shouldRedirectToInviteAuthenticated({
        membershipsLoadStatus: tenantMembershipLoadState?.status,
        hasTenantMembership,
        isInviteAuthenticated,
        isUserProfile
      })
    ) {
      return next({ name: "InviteAuthenticated", query: to.query, hash: to.hash })
    }

    return next()
  }

  if (isAuthRoute) return next()

  return next({ name: "Auth" })
})

// Update page title based on route
router.afterEach((to) => {
  const baseTitle = "Telarya"
  let pageTitle = null

  // Try to get title from route meta
  if (to.meta?.titleKey) {
    // Use i18n key if available
    try {
      pageTitle = i18n.global.t(to.meta.titleKey)
    } catch (e) {
      reportError(e, { context: "router.afterEach.titleKey", titleKey: to.meta.titleKey })
      console.warn(`Failed to translate titleKey: ${to.meta.titleKey}`)
    }
  } else if (to.meta?.title) {
    // Use static title from meta
    pageTitle = to.meta.title
  } else if (to.name && to.name !== "Index") {
    // Fallback to route name
    pageTitle = to.name
  }

  // Capitalize first letter if we have a title
  if (pageTitle) {
    pageTitle = pageTitle.charAt(0).toUpperCase() + pageTitle.slice(1)
  }

  // Get environment prefix (if any)
  const currentTitle = document.title
  const envPrefix = currentTitle.match(/^\[.\]\s/) ? currentTitle.match(/^\[.\]\s/)[0] : ""

  // Set final title
  if (pageTitle) {
    document.title = `${envPrefix}${pageTitle} - ${baseTitle}`
  } else {
    document.title = `${envPrefix}${baseTitle}`
  }
})

export default router
